import { useState, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  GoabModal,
  GoabButtonGroup,
  GoabButton,
} from "@abgov/react-components";
import FamilyMembersList from "./FamilyMembersList";
import FamilyMember from "./FamilyMember";
import {
  ADD_ANOTHER_FAMILY_MEMBER_BUTTON_TRACKING_FIELD,
  ADD_FAMILY_MEMBER_BUTTON_TRACKING_FIELD,
} from "../../utils/analytics-constants";
import { handleGA } from "../../utils/analytics";
import { useAnalyticsStep } from "../../utils/useAnalyticsStep";

export const initializeFamilyMembersFields = (fields, data) => {
  fields["familyMembers"] = data["familyMembers"] ?? [];
  fields["acceptLegalRepresentative"] =
    data["acceptLegalRepresentative"] ?? false;
};

const FamilyMembers = ({
  form,
  getResourceValue,
  getRefData,
  setShowStepActionButtons,
  setFormData,
  gaTracking,
  context = null,
  disabledFeatures = [],
  relationshipOptions = [
    { code: "child" },
    { code: "spouse" },
    { code: "adult" },
    { code: "elderly" },
  ],
}) => {
  const applicationData =
    context?.userApplicationData?.applicationData || form.values;
  const isGenerationTrackingEnabled =
    context?.programFeatures?.ApplicationGenerationTracking === true;
  const isEditable =
    context?.user?.isProxyUser || context?.canEditApplication !== false;

  const supportInfo = context?.supportInfo ?? "";

  const [familyMembers, setFamilyMembers] = useState(
    form?.values?.familyMembers ?? [],
  );
  const [selectedFamilyMember, setSelFamilyMember] = useState(null);
  const [showDeleteFamilyMember, setShowDeleteFamilyMember] = useState(null);

  const familyMemberWasChanged = useRef(false);
  const initializedGeneration = useRef(false);

  // Get the current application generation number (defaults to 0)
  const getApplicationGeneration = () => {
    return applicationData?.generation ?? 0;
  };

  useEffect(() => {
    context?.setHasFamilyMembers?.(familyMembers.length > 0);
  }, [familyMembers.length, context?.setHasFamilyMembers]);

  // Initialize generation field for existing family members that don't have it
  useEffect(() => {
    if (
      !initializedGeneration.current &&
      familyMembers?.length > 0 &&
      applicationData
    ) {
      const appGeneration = getApplicationGeneration();
      let hasUpdates = false;

      const updatedFamilyMembers = familyMembers.map((fm) => {
        if (fm.generation === undefined || fm.generation === null) {
          hasUpdates = true;
          return { ...fm, generation: appGeneration };
        }
        return fm;
      });

      if (hasUpdates) {
        setFamilyMembers(updatedFamilyMembers);
        const formValue = {
          ...form.values,
          familyMembers: updatedFamilyMembers,
        };
        form.setValues(formValue);
        if (setFormData) {
          setFormData(formValue);
        }
      }

      initializedGeneration.current = true;
    }
  }, [applicationData, familyMembers.length, form, setFormData]);

  const addUpdateFamilyMember = (familyMember) => {
    if (!!familyMember) {
      let updatedFamilyMembers = [...familyMembers];
      familyMemberWasChanged.current = true;
      if (!!familyMember.id) {
        // Update existing family member - preserve or initialize generation
        updatedFamilyMembers = familyMembers.map((fm) => {
          if (fm.id === familyMember.id) {
            // Preserve existing generation or use family member's generation, or default to app generation
            const generation =
              familyMember.generation ??
              fm.generation ??
              getApplicationGeneration();
            return { ...familyMember, generation };
          }
          return fm;
        });
      } else {
        // Assign application's current generation to new family member
        const newMember = {
          ...familyMember,
          id: uuidv4(),
          generation: getApplicationGeneration(),
        };
        updatedFamilyMembers = [...(familyMembers ?? []), newMember];
      }
      setFamilyMembers(updatedFamilyMembers);
      const formValue = {
        ...form.values,
        familyMembers: updatedFamilyMembers,
        acceptLegalRepresentative: !familyMemberWasChanged.current,
      };
      form.setValues(formValue);
      if (setFormData) {
        setFormData(formValue);
      }
    }
    setSelectedFamilyMember(null);
  };

  const deleteFamilyMember = (familyMemberIdToRemove) => {
    if (!!familyMemberIdToRemove) {
      const updatedFamilyMembers = familyMembers.filter(
        (fm) => fm.id !== familyMemberIdToRemove,
      );
      familyMemberWasChanged.current = true;
      setFamilyMembers(updatedFamilyMembers);
      const formValue = {
        ...form.values,
        familyMembers: updatedFamilyMembers,
        acceptLegalRepresentative: !familyMemberWasChanged.current,
      };
      form.setValues(formValue);
      if (setFormData) {
        setFormData(formValue);
      }
    }
    setShowDeleteFamilyMember(null);
    setSelectedFamilyMember(null);
  };

  const setSelectedFamilyMember = async (familyMember) => {
    const familyMemberToSet = familyMember;

    setSelFamilyMember(familyMemberToSet);

    // Expose the selected member on the step context so template conditions
    // can evaluate against it without persisting it to saved form data.
    context?.setSelectedFamilyMember?.(familyMemberToSet ?? null);

    if (setShowStepActionButtons) {
      setShowStepActionButtons(familyMemberToSet === null);
    }
  };

  const currentStep = useAnalyticsStep();

  const handleAddFamilyMemberClick = () => {
    // 1. Execute the functional logic
    setSelectedFamilyMember({});

    // 2. Determine which event to fire based on existing data
    const isFirstMember = !(familyMembers?.length > 0);
    const trackingFieldToFind = isFirstMember
      ? ADD_FAMILY_MEMBER_BUTTON_TRACKING_FIELD
      : ADD_ANOTHER_FAMILY_MEMBER_BUTTON_TRACKING_FIELD;

    // 3. Extract config and trigger GA
    if (gaTracking) {
      const config = gaTracking.find(
        (t) => t.trackingField === trackingFieldToFind,
      );
      if (config) {
        handleGA({ gaTracking: [config] }, currentStep);
      }
    }
  };

  return (
    <>
      {selectedFamilyMember !== null ? (
        <FamilyMember
          familyMember={selectedFamilyMember}
          setShowDeleteFamilyMember={setShowDeleteFamilyMember}
          addUpdateFamilyMember={addUpdateFamilyMember}
          getResourceValue={getResourceValue}
          getRefData={getRefData}
          applicationGeneration={getApplicationGeneration()}
          isGenerationTrackingEnabled={isGenerationTrackingEnabled}
          existingFamilyMembers={familyMembers}
          supportInfo={supportInfo}
          disabledFeatures={disabledFeatures}
          relationshipOptions={relationshipOptions}
        />
      ) : (
        <FamilyMembersList
          familyMembers={familyMembers}
          setSelectedFamilyMember={setSelectedFamilyMember}
          setShowDeleteFamilyMember={setShowDeleteFamilyMember}
          familyMemberWasChanged={familyMemberWasChanged}
          getResourceValue={getResourceValue}
          getRefData={getRefData}
          form={form}
          setFormData={setFormData}
          applicationGeneration={getApplicationGeneration()}
          isGenerationTrackingEnabled={isGenerationTrackingEnabled}
          isEditable={isEditable}
          handleAddFamilyMemberClick={handleAddFamilyMemberClick}
        />
      )}

      <GoabModal
        heading={getResourceValue({
          key: "RESOURCES@familyMembers.delete.confirm",
        })}
        role="alertdialog"
        open={!!showDeleteFamilyMember?.id}
        actions={
          <GoabButtonGroup alignment="end">
            <GoabButton
              type="secondary"
              onClick={() => setShowDeleteFamilyMember(null)}
            >
              {getResourceValue({ key: "RESOURCES@global.button.cancel" })}
            </GoabButton>
            <GoabButton
              variant="destructive"
              type="primary"
              onClick={() => deleteFamilyMember(showDeleteFamilyMember.id)}
            >
              {getResourceValue({ key: "RESOURCES@global.button.delete" })}
            </GoabButton>
          </GoabButtonGroup>
        }
      >
        <p>
          {getResourceValue({
            key: "RESOURCES@familyMembers.delete.content",
            params: {
              familyMemberName:
                showDeleteFamilyMember?.givenName +
                " " +
                showDeleteFamilyMember?.surname,
            },
          })}
        </p>
      </GoabModal>
    </>
  );
};

export default FamilyMembers;
